import React, { useEffect, useState, useCallback } from 'react';
import {
    Typography,
    List,
    ListItem,
    ListItemText,
    CardContent,
    CardHeader,
    Box,
} from "@mui/material";
import { Loader } from "../../../../Components";
import { fetchSettings, updateSetting } from "../Redux/Action";
import { useDispatch, useSelector } from "react-redux";
import { SettingCard } from "../../../../Components/Cards/SettingCard";
import { SettingsRenderer } from "./SettingsRenderer";
import { SettingsEditModal } from "./Modals/SettingsEditModal";
import {cleanName} from "../../../../Setup/Config/formats";
import { setModal } from "../../../Common/App";
import {usePermissions} from "../../../../Hooks/usePermissions";

export const SettingsTab = ({ category, actionMinWidth = 200, controlSize = 'small' }) => {
    
    const dispatch = useDispatch();
    const { hasPermission } = usePermissions();
    const [isLoading, setIsLoading] = useState(true);
    const [logoPreview, setLogoPreview] = useState(null);
    const [, setLogoFile] = useState(null);

    // Get settings from Redux store filtered by category
    const settings = useSelector(state => 
        state.settings.list.filter(item => item.category === category)
    );

    // Fetch settings data
    const fetchData = useCallback(async () => {
        setIsLoading(true);
        await fetchSettings({ 
            dispatch, 
            filters: { 
                paginate: false, 
                category: category 
            } 
        }).then((res) => {
            setIsLoading(false);
        });
    }, [dispatch, category]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Handle value change
    const handleValueChange = async (id, newValue, parameterType) => {
        // Prepare the payload for the API
        const payload = {
            id: id,
            parameter_value: parameterType === 'JSON' ? null : newValue,
            parameter_json: parameterType === 'JSON' ? newValue : null,
        };

        // Call the API to update the setting - Redux will handle the state update
        await updateSetting({ dispatch, data: payload }).then((res) => {
            if (res.success) {
            }
        });
    };

    // Handle edit setting
    const handleEditSetting = (setting) => {
        dispatch(setModal({
            component: <SettingsEditModal 
                setting={setting} 
                onClose={() => dispatch(setModal({ open: false }))}
                onSave={() => {
                    dispatch(setModal({ open: false }));
                    fetchData(); // Refresh data after save
                }}
            />,
            open: true
        }));
    };

    // Handle logo upload
    const handleLogoUpload = async (event) => {
        const file = event.target.files[0];
        setLogoPreview(URL.createObjectURL(file)); // Preview the logo locally
        setLogoFile(file);

        // Upload the logo to the server
        const formData = new FormData();
        formData.append("file", file);
        formData.append("type", "logo");

        try {
            const response = await fetch("/api/documents/upload", {
                method: "POST",
                body: formData,
            });
            const result = await response.json();

            if (result.success) {
                const documentId = result.document_id;
                // Update the logo_document_id via API - Redux will handle the state update
                const payload = {
                    id: settings.find(item => item.parameter_name === "logo_document_id")?.id,
                    parameter_value: documentId,
                };
                await updateSetting({ dispatch, data: payload });
            }
        } catch (error) {
        }
    };

    // Group settings by their ui_group property
    const groupedSettings = settings.reduce((groups, item) => {
        const group = item.ui_group || "General";
        if (!groups[group]) {
            groups[group] = [];
        }
        groups[group].push(item);
        return groups;
    }, {});

    Object.values(groupedSettings).forEach((groupItems) => {
        groupItems.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
    });


    return (
        <SettingCard>
            <CardHeader
                title={cleanName(category)}
                avatar={<Loader isLoading={isLoading} type={"linear"} />}
            />
            <CardContent>
                {isLoading ? (
                    <Typography variant="caption">Loading...</Typography>
                ) : Object.keys(groupedSettings).length === 0 ? (
                    <Typography variant="caption">No settings found</Typography>
                ) : (
                    Object.entries(groupedSettings).map(([group, groupItems]) => (
                        <Box key={group}>
                            <List>
                                {groupItems.map((item) => (
                                    <ListItem
                                        key={item.id}
                                        sx={{
                                            alignItems: 'flex-start',
                                            gap: 2,
                                        }}
                                    >
                                        <ListItemText
                                            sx={{
                                                flex: '1 1 auto',
                                                minWidth: 0,
                                                pr: 1,
                                            }}
                                            primary={
                                                hasPermission('core.settings.edit') ? (
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            cursor: 'pointer',
                                                            color: 'primary.main',
                                                            '&:hover': {
                                                                color: 'primary.dark',
                                                                textDecoration: 'underline'
                                                            },
                                                            transition: 'all 0.2s ease-in-out'
                                                        }}
                                                        onClick={() => handleEditSetting(item)}
                                                    >
                                                        {item.title}
                                                    </Typography>
                                                ) : (
                                                    item.title
                                                )
                                            }
                                            secondary={item.description}
                                            secondaryTypographyProps={{
                                                sx: {
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                },
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                flex: `0 0 ${actionMinWidth}px`,
                                                width: actionMinWidth,
                                                maxWidth: '42%',
                                                ...(item.type === 'switch'
                                                    ? {
                                                        display: 'flex',
                                                        justifyContent: 'flex-end',
                                                        alignItems: 'center',
                                                    }
                                                    : {}),
                                            }}
                                        >
                                            <SettingsRenderer
                                                setting={item}
                                                onValueChange={handleValueChange}
                                                logoPreview={logoPreview}
                                                onLogoUpload={handleLogoUpload}
                                                controlSize={controlSize}
                                                fullWidth
                                            />
                                        </Box>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    ))
                )}
            </CardContent>
        </SettingCard>
    );
};
